import { t } from "i18next";

import { CustomerFolderTypeCode } from "src/core/constants/folder";
import {
  CustomerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshotQuery,
  FolderFilesPageHrQuery,
  FolderFilesPageQuery,
  LastModifiedFolderFilePerCurrentCustomerFolderQuery,
  RiskLevel,
} from "src/core/gql/graphql";
import { IRiskDisplay } from "src/core/interfaces/Folders";
import { FolderListOf } from "src/core/types/folderListStrategy";

type CustomSummaryEC =
  | FolderFilesPageQuery["folderFilesPage"]["summaries"][number]
  | FolderFilesPageHrQuery["folderFilesPage"]["summaries"][number];

type CustomRiskStatusEC =
  | FolderFilesPageQuery["folderFilesPage"]["summaries"][number]["folderFile"]["riskStatus"]
  | FolderFilesPageHrQuery["folderFilesPage"]["summaries"][number]["folderFile"]["riskStatus"];

type RiskDisplayBuilderEC = (riskStatus: CustomRiskStatusEC | null | undefined) => IRiskDisplay;

type CustomerWithInputValues = {
  allInputValues: { inputField: { fieldCode: string }; stringValue?: string | null }[];
};

type FolderWithDetails = {
  id?: string | null;
  riskStatus?: CustomRiskStatusEC | null;
  currentCustomerFolder?: {
    owner?: { id?: string | null; firstName?: string | null; lastName?: string | null } | null;
    followingUsers?:
      | {
          id: string;
          firstName?: string | null;
          lastName?: string | null;
        }[]
      | null;
    customerFolderType?: {
      code?: string | null;
    } | null;
  } | null;
  adminStatus?: {
    lastModification?: string | null;
    lastModifier?: { name?: string | null } | null;
  } | null;
};

const getIdentity = (inputValuesMap: Record<string, string | null | undefined>, isPPH: boolean) => {
  if (isPPH) {
    let identity: string[] = [];
    if (
      inputValuesMap["firstName"] === t("form.createClient.emptyPPHName") ||
      inputValuesMap["lastName"] === t("form.createClient.emptyPPHName")
    ) {
      identity = [t("empty")];
    } else {
      if (inputValuesMap["firstName"]) {
        identity = [inputValuesMap["firstName"]];
      }
      if (inputValuesMap["lastName"]) {
        identity.push(inputValuesMap["lastName"]);
      }
    }
    return identity.join(" ");
  }
  return inputValuesMap["name"] ?? "";
};

/**
 * Fonction helper pour mapper les données communes d'un dossier.
 * Extrait la logique partagée entre mapECCommonFolderSummary et mapECValidatedFolderSummariesToFolderList.
 */
const mapECFolderSummaryFromExtractedData = (
  customer: CustomerWithInputValues,
  folder: FolderWithDetails,
  buildRiskDisplay: (riskStatus: CustomRiskStatusEC | null | undefined) => IRiskDisplay
): FolderListOf<"EC"> => {
  const inputValuesMap = Object.fromEntries(
    customer.allInputValues.map((entry) => [entry.inputField.fieldCode, entry.stringValue])
  );

  const riskStatus = folder?.riskStatus;

  const followers: { id: string; name: string }[] | undefined =
    folder?.currentCustomerFolder?.followingUsers
      ?.slice()
      .sort((a, b) => {
        const firstNameA = a.firstName ?? "";
        const firstNameB = b.firstName ?? "";
        return firstNameA.localeCompare(firstNameB);
      })
      .map((follower: { id: string; firstName?: string | null; lastName?: string | null }) => ({
        id: follower.id,
        name: `${follower.firstName ?? ""} ${follower.lastName ?? ""}`.trim(),
      })) ?? undefined;

  const owner = folder?.currentCustomerFolder?.owner;
  const inChargeValue = owner?.id ? `${owner.firstName ?? ""} ${owner.lastName ?? ""}`.trim() : "";
  const isPPH =
    folder?.currentCustomerFolder?.customerFolderType?.code === CustomerFolderTypeCode.PPH;

  return {
    id: folder?.id ?? "",
    identity: getIdentity(inputValuesMap, isPPH),
    registration: inputValuesMap["registration"] ?? "",
    inCharge: inChargeValue,
    riskDisplay: buildRiskDisplay(riskStatus),
    riskIcons: builRiskDisplayIcons(riskStatus),
    updatedAt: folder?.adminStatus?.lastModification ?? undefined,
    lastCompleter: folder?.adminStatus?.lastModifier?.name ?? undefined,
    followers,
    isPPH,
  } satisfies FolderListOf<"EC">;
};

/**
 * Map la partie commune des dossiers pour l'affichage dans la liste des dossiers
 * (VALIDATION_PENDING_HR et VALIDATION_PENDING).
 */
export const mapECCommonFolderSummary = (
  summary: CustomSummaryEC,
  buildRiskDisplay: (riskStatus: CustomRiskStatusEC | null | undefined) => IRiskDisplay
): FolderListOf<"EC"> => {
  return mapECFolderSummaryFromExtractedData(
    summary.customer,
    summary.folderFile,
    buildRiskDisplay
  );
};

export const buildECHrRiskDisplay: RiskDisplayBuilderEC = (riskStatus) => {
  const axes =
    riskStatus && "riskDetail" in riskStatus
      ? riskStatus.riskDetail?.axesStatus
          ?.filter((axis) => axis.riskLevel === RiskLevel.High)
          .map((axis) => {
            const filteredRisks = axis.risks
              .filter((risk) => risk.item === "PEP" || risk.item === "Sanction")
              .map((risk) => risk.item);

            return {
              axisName: axis.axisName,
              riskLevel: axis.riskLevel,
              risks: filteredRisks,
            };
          })
      : [];

  return {
    globalRiskLevel: riskStatus?.globalRiskLevel ?? "UNKNOWN",
    axes,
  };
};

/**
 * Map les risques des dossiers pour l'affichage dans la liste des dossiers (VALIDATION_PENDING_HR).
 * On a besoin de garder le détail des risques (Localisation, Client:Sanction, Client:PEP).
 */
export const mapECHrFolderSummariesToFolderList = (
  summaries: NonNullable<FolderFilesPageHrQuery["folderFilesPage"]>["summaries"]
): FolderListOf<"EC">[] => {
  return summaries.map<FolderListOf<"EC">>((summary) =>
    mapECCommonFolderSummary(summary, buildECHrRiskDisplay)
  );
};

/**
 * Map les risques des dossiers pour l'affichage dans la liste des dossiers (DRAFT).
 */
export const mapECCustomerFolderSummariesToFolderList = (
  summaries: NonNullable<
    CustomerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshotQuery["customerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshot"]
  >["summaries"]
): FolderListOf<"EC">[] => {
  return summaries.map((summary) => {
    const customer = summary.customer;
    const folder = summary.customerFolder;
    const file = summary.file;

    const inputValuesCustomerMap = Object.fromEntries(
      (customer.allInputValues ?? []).map((entry) => [
        entry.inputField.fieldCode,
        entry.stringValue,
      ])
    );

    const updatedAt = summary.mission?.renewDate ?? undefined;

    const isPPH = folder?.customerFolderType?.code === CustomerFolderTypeCode.PPH;

    return {
      id: folder.folderFiles?.summaries?.[0]?.id,
      identity: getIdentity(inputValuesCustomerMap, isPPH),
      registration: inputValuesCustomerMap["registration"] ?? "",
      inCharge: folder.owner ? `${folder.owner.firstName} ${folder.owner.lastName}` : "",
      riskDisplay: {
        globalRiskLevel: file?.riskStatus?.globalRiskLevel ?? "UNKNOWN",
        axes: [],
      },
      riskIcons: builRiskDisplayIcons(file?.riskStatus),
      lastCompleter: folder?.adminStatus?.lastModifier?.name ?? "",
      isPPH,
      updatedAt,
    } satisfies FolderListOf<"EC">;
  });
};

export const buildECRiskDisplay: RiskDisplayBuilderEC = (riskStatus) => {
  return {
    globalRiskLevel: riskStatus?.globalRiskLevel ?? "UNKNOWN",
    axes: [],
  };
};

export const builRiskDisplayIcons: RiskDisplayBuilderEC = (riskStatus) => {
  const axes =
    riskStatus && "riskDetail" in riskStatus
      ? riskStatus.riskDetail?.axesStatus?.map((axis) => {
          return {
            axisName: axis.axisName,
            riskLevel: axis.riskLevel,
          };
        })
      : [];

  return {
    globalRiskLevel: riskStatus?.globalRiskLevel ?? "UNKNOWN",
    axes,
  };
};

/**
 * Map les risques des dossiers pour l'affichage dans la liste des dossiers (VALIDATION_PENDING).
 * On ne garde ici que le risque global, on n'a pas besoin du détail des risques.
 */
export const mapECFolderSummariesToFolderList = (
  summaries: NonNullable<FolderFilesPageQuery["folderFilesPage"]>["summaries"]
): FolderListOf<"EC">[] => {
  return summaries.map<FolderListOf<"EC">>((summary) =>
    mapECCommonFolderSummary(summary, buildECRiskDisplay)
  );
};
/**
 * Map les risques des dossiers pour l'affichage dans la liste des dossiers (VALIDATED).
 * On ne garde ici le détail des risques.
 */
export const mapECValidatedFolderSummariesToFolderList = (
  content: NonNullable<
    LastModifiedFolderFilePerCurrentCustomerFolderQuery["lastModifiedFolderFilePerCurrentCustomerFolder"]["content"]
  >
): FolderListOf<"EC">[] => {
  return content.map<FolderListOf<"EC">>((item) => {
    // Le type retourné par la requête GraphQL a currentCustomer avec allInputValues
    const customer = (
      item as unknown as {
        currentCustomer: CustomerWithInputValues;
      }
    ).currentCustomer;
    const folder = (
      item as unknown as {
        lastModifiedFolderFile: FolderWithDetails;
      }
    ).lastModifiedFolderFile;

    return mapECFolderSummaryFromExtractedData(customer, folder, builRiskDisplayIcons);
  });
};
